import {
  Editor,
  type EditorProps,
  EditorUtils,
  ProseMirror,
} from '@progress/kendo-react-editor'
import React from 'react'
import { useFormContext } from 'react-hook-form'

import { useAuthorization } from '~/hooks/useAuthorization'
import { useFormField } from '~/hooks/useFormField'
import { sanitizeUnsafeContent } from '~/utils'

import { type InputValues } from '../../utils'

import { ImageView } from './nodeViews/ImageView'
import { copyStylesheetLinkPlugin } from './plugins/copyStylesheetLinkPlugin'
import { popoverLinkPlugin } from './plugins/selectedAnchorChangeEventPlugin'
import { stylesheetPlugin } from './plugins/stylesheetPlugin'
import { tools } from './tools'

const editorContentStyle: EditorProps['contentStyle'] = {}

export type DocumentReportEditEditorProps = {
  className?: string
}

export const DocumentReportEditEditor = ({
  className,
}: DocumentReportEditEditorProps) => {
  const { control } = useFormContext<InputValues>()
  const contentField = useFormField(control, 'content')
  const handleChange = React.useCallback<EH<EditorProps, 'onChange'>>(
    (event) => {
      contentField.onChange(sanitizeUnsafeContent(event.html))
    },
    [contentField],
  )
  // handleChange だけだと保存値は綺麗になるが、貼り付け内容はサニタイズ前にエディタ自身のDOMへ描画されてしまう
  // （貼った本人の画面にそのまま危険なリンクが生きた状態で表示される）
  // onPasteHtml は文書へ挿入される前に内容を差し替えられるので、ここで止める。
  const handlePasteHtml = React.useCallback<EH<EditorProps, 'onPasteHtml'>>(
    (event) => sanitizeUnsafeContent(event.pastedHtml),
    [],
  )

  const getAuthorization = useAuthorization()
  const [popoverLink, setPopoverLink] = React.useState<React.ReactNode>(null)
  const handleMount = React.useCallback<EH<EditorProps, 'onMount'>>(
    ({ dom, viewProps }) =>
      new ProseMirror.EditorView(
        { mount: dom },
        {
          ...viewProps,
          nodeViews: {
            ...viewProps.nodeViews,
            image: (node) => new ImageView({ getAuthorization, node }),
          },
          state: ProseMirror.EditorState.create({
            ...viewProps.state,
            plugins: [
              ...viewProps.state.plugins,
              EditorUtils.textHighlight(),
              copyStylesheetLinkPlugin(),
              stylesheetPlugin(),
              popoverLinkPlugin(setPopoverLink),
            ],
          }),
        },
      ),
    [getAuthorization],
  )
  const { value, ...rest } = contentField

  return (
    <>
      <Editor
        {...rest}
        className={className}
        contentStyle={editorContentStyle}
        defaultContent={value}
        tools={tools}
        onChange={handleChange}
        onMount={handleMount}
        onPasteHtml={handlePasteHtml}
      />

      {popoverLink}
    </>
  )
}
